// src/components/DownloadButton.js
import { useState, useCallback } from "react";
import { Button, Menu, MenuItem, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useData } from "../hooks/useData";

const DownloadButton = ({ canvas }) => {
  const { setSnackbar } = useData();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Check if canvas has objects
  const hasObjects = canvas && canvas.getObjects().length > 0;

  const handleClick = (event) => {
    if (!canvas) {
      setSnackbar({
        open: true,
        message: "Canvas not initialized.",
        severity: "error",
      });
      return;
    }
    if (hasObjects) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadFile = useCallback(
    (dataURL, extension) => {
      try {
        const link = document.createElement("a");
        link.href = dataURL;
        // Use timestamp for unique file names
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        link.download = `canvas_${timestamp}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbar({
          open: true,
          message: `Downloaded canvas as ${extension.toUpperCase()}.`,
          severity: "success",
        });
      } catch (error) {
        console.error("DownloadButton: Failed to download file", error);
        setSnackbar({
          open: true,
          message: "Error downloading file.",
          severity: "error",
        });
      }
    },
    [setSnackbar]
  );

  const handleDownloadJpeg = () => {
    try {
      const dataURL = canvas.toDataURL({
        format: "jpeg",
        quality: 0.8, // Add quality for JPEG
      });
      downloadFile(dataURL, "jpg");
    } catch (error) {
      console.error("DownloadButton: Failed to download JPEG", error);
      setSnackbar({
        open: true,
        message: "Error downloading JPEG.",
        severity: "error",
      });
    }
    handleClose();
  };

  const handleDownloadPng = () => {
    try {
      const dataURL = canvas.toDataURL({
        format: "png",
      });
      downloadFile(dataURL, "png");
    } catch (error) {
      console.error("DownloadButton: Failed to download PNG", error);
      setSnackbar({
        open: true,
        message: "Error downloading PNG.",
        severity: "error",
      });
    }
    handleClose();
  };

  const handleDownloadSvg = () => {
    try {
      const svgData = canvas.toSVG();
      const dataURL =
        "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgData);
      downloadFile(dataURL, "svg");
    } catch (error) {
      console.error("DownloadButton: Failed to download SVG", error);
      setSnackbar({
        open: true,
        message: "Error downloading SVG.",
        severity: "error",
      });
    }
    handleClose();
  };

  const handleDownloadJson = () => {
    try {
      const jsonData = canvas.toJSON([
        "id",
        "name",
        "fill",
        "stroke",
        "strokeWidth",
        "background",
        "left",
        "top",
        "scaleX",
        "scaleY",
        "radius",
        "width",
        "height",
        "angle",
        "opacity",
        "selectable",
        "evented",
        "originX",
        "originY",
        "flipX",
        "flipY",
        "points",
        "text",
        "fontFamily",
        "fontSize",
        "textAlign",
        "fontWeight",
        "fontStyle",
        "shadow",
      ]);
      const dataStr =
        "data:text/json;charset=utf-8," +
        encodeURIComponent(JSON.stringify(jsonData, null, 2));
      downloadFile(dataStr, "json");
    } catch (error) {
      console.error("DownloadButton: Failed to download JSON", error);
      setSnackbar({
        open: true,
        message: "Error downloading JSON.",
        severity: "error",
      });
    }
    handleClose();
  };

  return (
    <Tooltip
      title="Download Canvas"
      enterDelay={1000}
      leaveDelay={200}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "#454545",
            color: "#fff",
            fontSize: "12px",
            textAlign: "center",
            "& .MuiTooltip-arrow::before": {
              backgroundColor: "#454545",
            },
          },
        },
      }}
    >
      <span>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleClick}
          disabled={!hasObjects}
          aria-label="Download Canvas"
        >
          Download
        </Button>
        <Menu
          id="download-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          MenuListProps={{
            "aria-labelledby": "download-button",
          }}
        >
          <MenuItem onClick={handleDownloadJpeg}>Download as JPEG</MenuItem>
          <MenuItem onClick={handleDownloadPng}>Download as PNG</MenuItem>
          <MenuItem onClick={handleDownloadSvg}>Download as SVG</MenuItem>
          <MenuItem onClick={handleDownloadJson}>Download as JSON</MenuItem>
        </Menu>
      </span>
    </Tooltip>
  );
};

export default DownloadButton;
